import { lerpAngle } from '@jianghu/shared';
import { additive, overlay, QPose, sampleClip, type Clip, type Pose } from './pose';

export interface AnimProfile {
  joints: readonly string[];
  idle(t: number): Pose;
  locomotion(phase: number, run: number): Pose;
  clips: Record<string, Clip>;
  flinch: Pose;
  stun?(t: number): Pose;
  /** Metres per gait cycle at walk / run / sprint (keeps feet planted). */
  stride: [number, number, number];
  /** Reference speeds (m/s) for walk / run / sprint. */
  speeds: [number, number, number];
}

interface ActiveAction {
  name: string;
  clip: Clip;
  start: number;
  duration: number;
}

const SMOOTH_RATE = 24;

/**
 * Layered procedural animation state machine:
 *   idle ⟷ locomotion (speed-blended, distance-driven gait phase)
 *   + one-shot actions (attacks, techniques, dodge) with fade in/out
 *   + stun / death overrides + additive hit flinch,
 * followed by exponential pose smoothing so no transition ever snaps.
 */
export class AnimationController {
  private t = 0;
  private phase = 0;
  private moveW = 0;
  private run = 0;
  private action: ActiveAction | null = null;
  private actionW = 0;
  private flinchT = 0;
  private stunW = 0;
  private dead = false;
  private deadAt = 0;
  private reviving: ActiveAction | null = null;
  private readonly out: QPose;
  private initialized = false;
  stunned = false;

  constructor(private readonly profile: AnimProfile) {
    this.out = QPose.fromPose(profile.idle(0), profile.joints);
  }

  get currentAction(): string | null {
    return this.action?.name ?? null;
  }

  /** Plays a one-shot clip, time-scaled to `durationSec` when given (matches server lock time). */
  play(name: string, durationSec?: number): boolean {
    const clip = this.profile.clips[name];
    if (!clip) return false;
    this.action = { name, clip, start: this.t, duration: durationSec ?? clip.duration };
    return true;
  }

  cancelAction(): void {
    this.action = null;
  }

  flinch(): void {
    this.flinchT = 0.25;
  }

  setDead(dead: boolean): void {
    if (dead && !this.dead) {
      this.deadAt = this.t;
      this.action = null;
    }
    if (!dead && this.dead) {
      const clip = this.profile.clips.REVIVE;
      this.reviving = clip ? { name: 'REVIVE', clip, start: this.t, duration: clip.duration } : null;
    }
    this.dead = dead;
  }

  update(dt: number, speed: number): QPose {
    this.t += dt;
    const p = this.profile;
    const [walkRef, runRef, sprintRef] = p.speeds;
    // Gait parameter: 0 walk .. 1 run .. 2 sprint.
    let runTarget = 0;
    if (speed > walkRef) runTarget = Math.min(1, (speed - walkRef) / Math.max(0.01, runRef - walkRef));
    if (speed > runRef * 1.06)
      runTarget = 1 + Math.min(1, (speed - runRef * 1.06) / Math.max(0.01, sprintRef - runRef * 1.06));
    this.run += (runTarget - this.run) * (1 - Math.exp(-8 * dt));
    const moveTarget = speed > 0.25 ? 1 : 0;
    this.moveW += (moveTarget - this.moveW) * (1 - Math.exp(-12 * dt));
    const stride =
      this.run <= 1
        ? p.stride[0] + (p.stride[1] - p.stride[0]) * this.run
        : p.stride[1] + (p.stride[2] - p.stride[1]) * (this.run - 1);
    this.phase = (this.phase + (speed * dt) / stride) % 1;

    const target = QPose.fromPose(p.idle(this.t), p.joints);
    if (this.moveW > 0.001) {
      const loco = QPose.fromPose(p.locomotion(this.phase, this.run), p.joints, target);
      target.blend(loco, this.moveW);
    }

    this.stunW += ((this.stunned && !this.dead ? 1 : 0) - this.stunW) * (1 - Math.exp(-10 * dt));
    if (this.stunW > 0.01 && p.stun) overlay(target, p.stun(this.t), this.stunW);

    if (this.action) {
      const elapsed = this.t - this.action.start;
      const u = elapsed / this.action.duration;
      if (u >= 1 && !this.action.clip.hold) {
        this.actionW = Math.max(0, this.actionW - dt / 0.12);
        if (this.actionW <= 0) this.action = null;
      } else {
        this.actionW = Math.min(1, this.actionW + dt / 0.05);
      }
      if (this.action) overlay(target, sampleClip(this.action.clip, Math.min(1, u)), this.actionW);
    }

    if (this.dead) {
      const clip = p.clips.DEATH;
      if (clip) overlay(target, sampleClip(clip, Math.min(1, (this.t - this.deadAt) / clip.duration)), 1);
    } else if (this.reviving) {
      const u = (this.t - this.reviving.start) / this.reviving.duration;
      if (u >= 1) this.reviving = null;
      else overlay(target, sampleClip(this.reviving.clip, u), 1 - Math.max(0, (u - 0.8) / 0.2));
    }

    if (this.flinchT > 0) {
      this.flinchT = Math.max(0, this.flinchT - dt);
      additive(target, p.flinch, Math.sin(Math.PI * (1 - this.flinchT / 0.25)));
    }

    if (!this.initialized) {
      this.out.copy(target);
      this.initialized = true;
      return this.out;
    }
    // Smooth towards the target pose; root yaw uses shortest-angle interpolation (spin attacks).
    const k = 1 - Math.exp(-SMOOTH_RATE * dt);
    const yaw = lerpAngle(this.out.root.rootYaw, target.root.rootYaw, k);
    this.out.blend(target, k);
    this.out.root.rootYaw = yaw;
    return this.out;
  }
}
